import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenericDialog } from "@/components/dialogs";
import { highlight } from "@/util";
import { StandardTest } from "@/api/williams/tests";
import { TestEvent } from "@/api/williams/testEvents";
import { useDebounce } from "@/hooks";
import { useState } from "react";
import { useTestLookup } from "../hooks";

import styles from "./TestSelection.tsx.module.css";

export type TestSelectionProps = {
  testEvent?: TestEvent;
  initialTests: StandardTest[];
  contractorId?: string;
  onAccept: (tests: StandardTest[]) => void;
};

export function TestSelectionDialog(props: TestSelectionProps) {
  const [selected, setSelected] = useState<StandardTest[]>(props.initialTests);
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const tests = useTestLookup(debouncedSearch, props.contractorId);

  function isSelected(test: StandardTest) {
    return selected.some((x) => x.recordId === test.recordId);
  }

  function handleSelectionChanged(test: StandardTest, checked: boolean) {
    setSelected((prev) => (checked ? [...prev, test] : prev.filter((x) => x.recordId !== test.recordId)));
  }

  return (
    <GenericDialog
      caption="Select Test(s)"
      buttons="OKCancel"
      className={styles.dialog}
      onAccept={() => props.onAccept(selected)}
    >
      <>
        <div className="input-group" style={{ maxWidth: 400 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <span className="input-group-text" role="button" onClick={() => tests.refetch()}>
            <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
          </span>
        </div>

        <div className={styles.container}>
          {tests.isLoading ? (
            <p>Loading...</p>
          ) : tests.isError ? (
            <p>An unknown error occurred.</p>
          ) : debouncedSearch && tests.data?.length === 0 ? (
            <p>Your search returned no results.</p>
          ) : tests.data?.length === 0 ? (
            <p>No tests found.</p>
          ) : (
            <table className="table table-sm table-borderless">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tests.data?.map((test) => (
                  <tr key={test.recordId}>
                    <td>{highlight(test.templateNumber, debouncedSearch)}</td>
                    <td className="text-end pe-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected(test)}
                        disabled={props.testEvent?.welderTests.some(
                          (x) => x.templateId === test.recordId && x.submitted
                        )}
                        onChange={(e) => handleSelectionChanged(test, e.currentTarget.checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    </GenericDialog>
  );
}
